import React from 'react';
import {Bell} from 'lucide-react';
import {
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    Badge,
    ListGroup,
    ListGroupItem,
    Button
} from 'reactstrap';
import {Link, useNavigate} from 'react-router-dom';
import {
    useGetUnreadCount,
    useGetNotifications,
    useMarkNotificationRead,
    useMarkAllNotificationsRead
} from '../../api/notifications/useNotifications';
import {RoutePaths} from '../../RoutePaths';

const NotificationBell: React.FC = () => {
    const navigate = useNavigate();
    const {data: unreadData} = useGetUnreadCount();
    const {data: notifications} = useGetNotifications(5);
    const markRead = useMarkNotificationRead();
    const markAllRead = useMarkAllNotificationsRead();

    const unreadCount = unreadData?.count ?? 0;
    const recentNotifications = notifications ?? [];

    const handleNotificationClick = (id: string, actionUrl?: string) => {
        if (!recentNotifications.find(n => n.id === id)?.isRead) {
            markRead.mutate(id);
        }
        if (actionUrl) {
            navigate(actionUrl);
        }
    };

    return (
        <UncontrolledDropdown nav inNavbar className="me-2 notification-bell">
            <DropdownToggle
                nav
                className="position-relative d-flex align-items-center p-2"
                id="notification-bell-btn"
                style={{cursor: 'pointer'}}
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <Badge
                        color="danger"
                        pill
                        className="position-absolute"
                        style={{
                            top: '4px',
                            right: '2px',
                            fontSize: '0.65rem',
                            padding: '0.25em 0.4em'
                        }}
                    >
                        {unreadCount}
                    </Badge>
                )}
            </DropdownToggle>

            <DropdownMenu
                end
                className="p-0 shadow-sm border border-light notification-dropdown"
                style={{
                    minWidth: '340px',
                    maxWidth: '380px',
                    marginTop: '0.4rem'
                }}
            >
                <div className="d-flex justify-content-between align-items-center bg-light border-bottom p-2 px-3 rounded-top">
                    <span className="fw-bold">Notifications</span>
                    {unreadCount > 0 && (
                        <Button
                            color="link"
                            size="sm"
                            className="p-0 text-decoration-none text-muted"
                            style={{fontSize: '0.85rem'}}
                            onClick={() => markAllRead.mutate()}
                        >
                            Mark all read
                        </Button>
                    )}
                </div>

                <ListGroup flush>
                    {recentNotifications.length === 0 ? (
                        <ListGroupItem className="text-center text-muted p-4 border-0">
                            No new notifications
                        </ListGroupItem>
                    ) : (
                        recentNotifications.map(notification => (
                            <ListGroupItem
                                key={notification.id}
                                action
                                tag="button"
                                onClick={() =>
                                    handleNotificationClick(
                                        notification.id,
                                        notification.actionUrl
                                    )
                                }
                                className={`p-3 border-start-0 border-end-0 border-bottom notification-item ${!notification.isRead ? 'bg-light' : ''}`}
                                style={{
                                    cursor: 'pointer',
                                    outline: 'none',
                                    textAlign: 'left'
                                }}
                            >
                                <div className="d-flex w-100 justify-content-between align-items-start mb-1 gap-2">
                                    <h6
                                        className={`mb-0 d-flex align-items-center ${!notification.isRead ? 'fw-bold' : ''}`}
                                        style={{
                                            fontSize: '0.95rem',
                                            minWidth: 0
                                        }}
                                    >
                                        {!notification.isRead && (
                                            <span
                                                className="bg-primary rounded-circle d-inline-block me-2 flex-shrink-0"
                                                style={{
                                                    width: '8px',
                                                    height: '8px'
                                                }}
                                                title="Unread"
                                            ></span>
                                        )}
                                        <span
                                            className="text-truncate"
                                            title={notification.title}
                                        >
                                            {notification.title}
                                        </span>
                                    </h6>
                                    <small
                                        className="text-muted flex-shrink-0"
                                        style={{fontSize: '0.75rem'}}
                                    >
                                        {(() => {
                                            const diff =
                                                new Date().getTime() -
                                                new Date(
                                                    notification.createdAt
                                                ).getTime();
                                            const hours = Math.floor(
                                                diff / (1000 * 60 * 60)
                                            );
                                            if (hours < 24)
                                                return `${hours}h ago`;
                                            return `${Math.floor(hours / 24)}d ago`;
                                        })()}
                                    </small>
                                </div>
                                <p
                                    className="mb-0 text-muted"
                                    style={{
                                        fontSize: '0.85rem',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        paddingRight: '0.5rem'
                                    }}
                                >
                                    {notification.description}
                                </p>
                            </ListGroupItem>
                        ))
                    )}
                </ListGroup>

                <div className="p-2 text-center bg-light rounded-bottom">
                    <Link
                        to={RoutePaths.notifications.root()}
                        className="text-decoration-none w-100 d-block py-1 fw-bold"
                    >
                        View all notifications
                    </Link>
                </div>
            </DropdownMenu>

            <style>{`
                .notification-dropdown .list-group-item:last-child {
                    border-bottom: 0 !important;
                }
                .notification-dropdown {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                }
                .notification-item:hover {
                    background-color: #f8f9fa !important;
                }
                .notification-item:active {
                    background-color: #e9ecef !important;
                }
            `}</style>
        </UncontrolledDropdown>
    );
};

export default NotificationBell;
